import { useEffect, useMemo, useRef, type MouseEvent, type RefObject } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Button, Typography, Input, notification, Spin } from "antd";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { oldBaseApi } from "@/entities/api";
import { journalsQueries, JournalRecordPageShell } from "@/entities/journal";
import { useFlags } from "@/shared/lib/flags";
import { useCoordinator, useLocalUser } from "@/shared/lib/hooks";
import { useUnifiedAutoResize } from "@/shared/utils";
import { useAppRoutes } from "@shared/lib/react-router";
import {
  useCreateSectionThreeEntry,
  useUpdateSectionThreeEntry,
  useUpdateSectionThreeWithSign,
} from "../journal-section-form/model/hooks/use-section-one-entry";
import {
  sectionThirdFormSchema,
  SectionThirdFormType,
  SectionThreeLocationType,
  sectionThreeLocationTypeValues,
} from "../journal-section-form/model/types";
import { buildSectionThreeUpdateDocs } from "../journal-section-form/model/utils/build-section-three-update-docs";
import {
  SectionDocumentList,
  SectionGeneralInfo,
  SectionSignerSelect,
} from "../journal-section-form/ui";
import "./journal-section-form-page.scss";

const { Title } = Typography;

export const JournalSectionFormPage = () => {
  const navigate = useNavigate();
  const { itemNoVersionId: itemNoVersionIdParam } = useParams<{ itemNoVersionId?: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const localUser = useLocalUser();
  const { titleId, mainJournalId, projectId, objectId } = useCoordinator();
  const appRoutes = useAppRoutes();
  const isVersionsEnabled = useFlags("ojrFormationFlowEnabledV1") === true;
  
  const isEditMode = itemNoVersionIdParam && itemNoVersionIdParam !== "new";
  const itemNoVersionId = isEditMode ? itemNoVersionIdParam : null;
  const workTypeParam = searchParams.get("workType");
  
  const { data: itemInfo, isFetching: isItemFetching } = useQuery({
    ...journalsQueries.getSectionThreeItem({
      itemNoVersionId: itemNoVersionId || "",
    }),
    enabled: !!itemNoVersionId,
  });
  
  // Use workType from itemInfo when editing, otherwise from URL params
  const workType = isEditMode && itemInfo?.workType 
    ? itemInfo.workType 
    : (workTypeParam || "WORK");
  const { data: mainJournalData } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  const { data: titleJournal } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const signersList = useMemo(
    () => [
      ...(titleJournal?.mainJournal?.titleJournal?.buildingContractor
        ?.representatives ?? []),
      ...(titleJournal?.mainJournal?.titleJournal?.buildingContractor
        ?.constructionControls ?? []),
    ],
    [titleJournal],
  );

  const resolvedItemNoVersionId = itemInfo?.itemNoVersionId || itemNoVersionId || "";

  const actConnectFromSectionListCache = useMemo(() => {
    if (!resolvedItemNoVersionId) return false;

    const cachedQueries = queryClient.getQueriesData({
      predicate: (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key)) return false;
        const hasSection3 = key.some(
          (k) =>
            typeof k === "string" &&
            k.includes("/api/v1/journals/section-three/"),
        );
        const hasRestriction = key.some(
          (k) => typeof k === "string" && k.includes("/restriction"),
        );
        return hasSection3 && hasRestriction;
      },
    });

    const found = cachedQueries.find(([, data]) => {
      const section3List = (data as any)?.section3List;
      if (!Array.isArray(section3List)) {
        return false;
      }
      const item = section3List.find(
        (x: any) => x?.itemNoVersionId === resolvedItemNoVersionId,
      );
      return item?.actConnect;
    });
    return !!found;

    return false;
  }, [queryClient, resolvedItemNoVersionId]);

  const { data: executionData } = useQuery({
    queryKey: ["section3ExecutionActs", resolvedItemNoVersionId],
    queryFn: async () => {
      const response = await oldBaseApi.post<{
        entityList?: unknown[];
      }>(`/api/work_three/by-ozhr-item-id/${resolvedItemNoVersionId}/acts`, {
        pageNumber: 0,
        pageSize: 1,
        searchValue: "",
        statuses: [
          "NEW",
          "RECREATED",
          "REJECTED",
          "SIGNED",
          "SIGNING",
          "APPROVAL",
          "APPROVED",
        ],
        actDateStart: "2024-05-01",
        actDateEnd: new Date().toISOString().split("T")[0],
      });
      return response.data;
    },
    enabled: !!resolvedItemNoVersionId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<SectionThirdFormType>({
    resolver: zodResolver(sectionThirdFormSchema),
    defaultValues: {
      companyList: [],
      docs: [],
      actionDetails: "",
    },
  });


  const { mutate: createEntry, isPending: isCreatePending } =
    useCreateSectionThreeEntry(
      mainJournalData?.mainJournal?.section3?.sectionListId || "",
    );

  const originalDocsRef = useRef<any[]>([]);

  const versionId = itemInfo?.itemNoVersionId || itemNoVersionId || "";
  const currentVersionId = itemInfo?.currentVersionId || "";
  const sectionThreeListId =
    mainJournalData?.mainJournal?.section3?.sectionListId || "";
  const { mutate: updateEntrySigned, isPending: isUpdateSignedPending } =
    useUpdateSectionThreeWithSign(versionId, sectionThreeListId);
  const { mutate: updateEntryNew, isPending: isUpdatePending } =
    useUpdateSectionThreeEntry(sectionThreeListId);

  const isSaving =
    isCreatePending || isUpdateSignedPending || isUpdatePending;
  const isFormLoading = Boolean(isEditMode && isItemFetching && !itemInfo);
  const showPageLoader = isFormLoading || isSaving;
  const loaderTip = isSaving ? "Сохранение..." : "Загрузка...";

  useEffect(() => {
    if (itemInfo && signersList.length > 0) {
      const companies = itemInfo.companyList?.filter(
        (c) => c.companyId && c.projectParticipantId,
      );
      if (companies?.length) {
        setValue(
          "companyList",
          companies.map((c) => ({
            companyId: c.companyId!,
            projectParticipantId: c.projectParticipantId!,
          })),
        );
      }
      setValue("workDate", itemInfo.workDate || '');
      setValue("workType", itemInfo?.workType || "");
      setValue("workName", itemInfo?.workName || "");
      setValue("workCondition", itemInfo?.workCondition || "");
      setValue("locationDescription", itemInfo?.locationDescription || "");
      if (
        itemInfo.locationType &&
        (sectionThreeLocationTypeValues as readonly string[]).includes(
          itemInfo.locationType,
        )
      ) {
        setValue("locationType", itemInfo.locationType as SectionThreeLocationType);
      }
      setValue(
        "signerId",
        itemInfo?.signer?.userId &&
          signersList.some(
            (signer) => signer?.user?.userId === itemInfo.signer?.userId,
          )
          ? itemInfo.signer.userId
          : "",
      );
      const converted = itemInfo?.docs?.map((doc) => ({
        id: doc.id,
        docId: doc.docId!,
        docType: doc.docType!,
        docTitle: doc.name!,
        documentUrl: doc.documentUrl!,
      }));

      originalDocsRef.current = converted || [];

      // @ts-expect-error setValue аргумент не совпадает с типом поля формы
      setValue("docs", converted, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [itemInfo, signersList, setValue]);


  const isConnectedToExecutionActs =
    actConnectFromSectionListCache ||
    !!(itemInfo as any)?.actConnect ||
    !!executionData?.entityList?.length;
  const isEditLockedByExecutionLink = isEditMode && isConnectedToExecutionActs;

  const isCurrentUserSigner = itemInfo?.signer?.userId === localUser?.user_id;

  const shouldShowVersioningNotice =
    isVersionsEnabled &&
    itemInfo &&
    itemInfo.status !== "NEW" &&
    isCurrentUserSigner;

  const handleClose = (e?: MouseEvent) => {
    if (showPageLoader) return;
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    navigate(
      appRoutes.journals.section3(projectId, objectId, mainJournalId, titleId),
    );
  };

  const onSubmit = (data: SectionThirdFormType) => {
    if (isEditLockedByExecutionLink) {
      return;
    }
    if (!data.companyList?.length) {
      notification.error({
        message: "Добавьте организацию / участника",
      });
      return;
    }
    const companyList = data.companyList.map(
      ({ companyId, projectParticipantId }) => ({
        companyId,
        projectParticipantId,
      }),
    );
    const updateDocs = buildSectionThreeUpdateDocs(
      data.docs,
      originalDocsRef.current,
    );
    if (itemNoVersionId) {
      const status = itemInfo?.status;
      if (status === "SIGNED") {
        updateEntrySigned(
          {
            body: {
              mainJournalId: mainJournalData?.mainJournal?.mainJournalId || "",
              section3Id:
                mainJournalData?.mainJournal?.section3?.sectionId || "",
              section3ListId:
                mainJournalData?.mainJournal?.section3?.sectionListId || "",
              companyList,
              workDate: data.workDate,
              workType: (itemInfo?.workType as "WORK" | "TEST") || "WORK",
              workName: data.workName,
              workCondition: data.workCondition,
              locationDescription: data.locationDescription,
              locationType: data.locationType,
              signerId: data.signerId,
              actionDetails: data.actionDetails || "",
              updateDocs,
            },
            params: {
              path: {
                sectionThreeListId,
                versionId: currentVersionId,
              },
            },
          },
          {
            onSuccess: () => {
              reset();
              navigate(-1);
            },
          },
        );
      } else {
        updateEntryNew(
          {
            body: {
              mainJournalId: mainJournalData?.mainJournal?.mainJournalId || "",
              section3Id:
                mainJournalData?.mainJournal?.section3?.sectionId || "",
              section3ListId:
                mainJournalData?.mainJournal?.section3?.sectionListId || "",
              companyList,
              workDate: data.workDate,
              workType: (itemInfo?.workType as "WORK" | "TEST") || "WORK",
              workName: data.workName,
              workCondition: data.workCondition,
              locationDescription: data.locationDescription,
              locationType: data.locationType,
              signerId: data.signerId,
              updateDocs,
            },
            params: {
              path: {
                itemNoVersionId: versionId,
              },
            },
          },
          {
            onSuccess: () => {
              reset();
              navigate(-1);
            },
          },
        );
      }
    } else {
      createEntry(
        {
          body: {
            mainJournalId: mainJournalData?.mainJournal?.mainJournalId || "",
            section3Id: mainJournalData?.mainJournal?.section3?.sectionId || "",
            section3ListId:
              mainJournalData?.mainJournal?.section3?.sectionListId || "",
            companyList,
            workDate: data.workDate,
            workType: (workType as "WORK" | "TEST") || "WORK",
            workName: data.workName,
            workCondition: data.workCondition,
            locationDescription: data.locationDescription,
            locationType: data.locationType,
            signerId: data.signerId,
            docs: data?.docs?.filter((doc: any) => doc?.docId),
          },
          params: {
            path: {
              sectionThreeListId:
                mainJournalData?.mainJournal?.section3?.sectionListId || "",
            },
          },
        },
        {
          onSuccess: () => {
            reset();
            navigate(-1);
          },
        },
      );
    }
  };

  // Автоматическое обновление высоты iframe в unified режиме
  const wrapperRef = useRef<HTMLDivElement>(null);
  useUnifiedAutoResize(wrapperRef as RefObject<HTMLElement>);

  return (
    <JournalRecordPageShell ref={wrapperRef} onClose={() => handleClose()}>
      <Spin
        spinning={showPageLoader}
        size="large"
        tip={loaderTip}
        className="journal-section-form-page-spin"
      >
        <div className="journal-section-form-page-title-block">
          <Title level={3}>
            {(() => {
              if (itemInfo) {
                return workType === "TEST"
                  ? "Редактирование записи об испытании раздел 3 ОЖР"
                  : "Редактирование записи о работе раздел 3 ОЖР";
              }
              return workType === "TEST"
                ? "Добавление записи об испытании в раздел 3 ОЖР"
                : "Добавление записи о работе в раздел 3 ОЖР";
            })()}
          </Title>
        </div>

        {shouldShowVersioningNotice ? (
          <div className="edit-notice">
            В случае внесения изменений в записи общего журнала в электронной
            форме создается новая версия записи. Запись нужно будет подписать
            заново. Понадобится заполнить поле о причинах изменений.
          </div>
        ) : null}

        {isEditLockedByExecutionLink ? (
          <div className="edit-notice">
            Редактировать запись недоступно, потому что установлена связь с ИД.
            Для возможности выполнить действие обратитесь к автору акта по
            исполнительной документации.
          </div>
        ) : null}

        <Form onFinish={handleSubmit(onSubmit)} layout="vertical">
          <SectionGeneralInfo
            control={control}
            setValue={setValue}
            trigger={trigger}
            initialLocationType={itemInfo?.locationType}
            disabled={!!isEditLockedByExecutionLink}
            titleJournal={titleJournal as any}
            isCreateMode={!isEditMode}
            initialCompanyList={itemInfo?.companyList}
          />

          <SectionDocumentList
            setValue={setValue}
            control={control}
            disabled={!!isEditLockedByExecutionLink}
          />

          <SectionSignerSelect 
            control={control} 
            signersList={signersList}
            disabled={!!isEditLockedByExecutionLink}
          />

          {itemInfo && itemInfo.status !== "NEW" && (
            <Form.Item
              label="Причина изменения записи"
              validateStatus={errors.actionDetails ? "error" : ""}
              help={errors.actionDetails?.message?.toString()}
            >
              <Controller
                name="actionDetails"
                control={control}
                rules={{ required: "Укажите причину изменения записи" }}
                render={({ field }) => (
                  <Input.TextArea
                    {...field}
                    rows={4}
                    placeholder="Опишите причину изменения записи"
                    disabled={!!isEditLockedByExecutionLink}
                    maxLength={255}
                    showCount
                    onChange={(e) => field.onChange(e.target.value.slice(0, 255))}
                  />
                )}
              />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              style={{ height: 44 }}
              disabled={!!isEditLockedByExecutionLink || showPageLoader}
            >
              {!itemInfo
                ? "Добавить запись"
                : "Обновить запись"}
            </Button>
            <Button
              onClick={(e) => handleClose(e)}
              disabled={showPageLoader}
              style={{ marginLeft: 8, height: 44 }}
            >
              Отмена
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </JournalRecordPageShell>
  );
};

